import { useEffect } from 'react';

/**
 * Publishes the REAL visible height of the viewport as `--app-h` on
 * <html>, so chat shells can size themselves to what the founder can
 * actually see.
 *
 * Three heights disagree on a phone and only one of them is right:
 *
 *   - `100vh` is the LARGEST the viewport gets — it assumes Safari's
 *     bottom toolbar is retracted. It never shrinks back when the bar
 *     is showing, so a shell sized to 100vh puts its pinned composer
 *     ~90px under the toolbar. That is the bug the founder shot on
 *     2026-10-03: the send button half-covered by the URL bar.
 *   - `100dvh` tracks the toolbar correctly and is the right default.
 *     It does NOT track the keyboard: iOS Safari does not resize the
 *     layout viewport when the keyboard opens, so a dvh shell keeps
 *     its full height and the composer ends up behind the keys.
 *   - `visualViewport.height` is what is actually visible, keyboard
 *     included. It is the only one that survives both.
 *
 * So: shells declare `h-[var(--app-h,100dvh)]` — dvh is the fallback
 * for the first paint and for anything without visualViewport, and
 * this hook overwrites it with the measured height from then on.
 *
 * Mounted once at the router root. Writing a CSS variable rather than
 * returning a number is deliberate: the value changes on every
 * keyboard open/close and toolbar scroll, and a state update there
 * would re-render the whole tree on each frame of the animation.
 */
export function useAppHeight() {
  useEffect(() => {
    const root = document.documentElement;
    const vv = window.visualViewport;

    // iOS fires visualViewport 'scroll' continuously while the toolbar
    // collapses, and every setProperty invalidates style for the whole
    // document — so only write when the rounded pixel actually moved.
    let last = -1;
    const apply = () => {
      const height = Math.round(vv ? vv.height : window.innerHeight);
      if (height === last) return;
      last = height;
      root.style.setProperty('--app-h', `${height}px`);
    };

    apply();

    if (!vv) {
      window.addEventListener('resize', apply);
      return () => window.removeEventListener('resize', apply);
    }

    // `resize` covers the keyboard and the toolbar changing size;
    // `scroll` covers iOS panning the visual viewport inside the
    // layout one, which changes what is visible without a resize.
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, []);
}
